import React, { useCallback } from "react"
import type { PanInfo, Variants } from "framer-motion"
import { motion, useDragControls } from "framer-motion"
import { useColors, useTheme } from "@mallardbay/lib-react-components"

import { pickDataAttributes } from "~components/shared/motion/motion-helpers"
import { componentStylesCss } from "~utils/helpers/style-helpers"

const DRAG_CLOSE_OFFSET_PX = 120
const DRAG_CLOSE_VELOCITY_PX = 900
const DRAG_CONSTRAINT_BOTTOM_PX = 260
const DRAG_CONSTRAINTS = {
    top: 0,
    bottom: DRAG_CONSTRAINT_BOTTOM_PX,
} as const

interface Props {
    readonly children: React.ReactNode
    readonly dragHandleAriaLabel: string
    readonly height: React.CSSProperties["height"]
    readonly onClose: () => void
    readonly testId?: string
    readonly variants: Variants
    readonly [dataAttribute: `data-${string}`]: string | undefined
}

export default function BottomSheet({
    children,
    dragHandleAriaLabel,
    height,
    onClose,
    testId,
    variants,
    ...rest
}: Props) {
    const styles = useStyles(height)
    const dragControls = useDragControls()
    const dataProps = pickDataAttributes(rest)

    const handleDragHandlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLButtonElement>) => {
            dragControls.start(event)
        },
        [dragControls]
    )

    const handleSheetDragEnd = useCallback(
        (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
            const shouldClose =
                info.offset.y > DRAG_CLOSE_OFFSET_PX ||
                info.velocity.y > DRAG_CLOSE_VELOCITY_PX

            if (shouldClose) {
                onClose()
            }
        },
        [onClose]
    )

    return (
        <motion.div style={styles.root} {...dataProps}>
            <motion.div
                data-testid={testId}
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
                drag="y"
                dragControls={dragControls}
                dragConstraints={DRAG_CONSTRAINTS}
                dragElastic={0.08}
                dragListener={false}
                dragMomentum={false}
                dragSnapToOrigin
                onDragEnd={handleSheetDragEnd}
                style={styles.sheet}
                {...dataProps}
            >
                <button
                    aria-label={dragHandleAriaLabel}
                    onPointerDown={handleDragHandlePointerDown}
                    style={styles.dragHandle}
                    type="button"
                >
                    <span style={styles.dragHandleIndicator} />
                </button>
                {children}
            </motion.div>
        </motion.div>
    )
}

function useStyles(height: React.CSSProperties["height"]) {
    const colors = useColors()
    const theme = useTheme()

    return componentStylesCss({
        root: {
            position: "fixed",
            right: 0,
            bottom: 0,
            left: 0,
            top: 0,
            zIndex: theme.zIndices.overlay,
        },
        sheet: {
            position: "absolute",
            right: 0,
            bottom: 0,
            left: 0,
            height,
            backgroundColor: colors.bg,
            borderTop: `1px solid ${colors.border}`,
            borderTopLeftRadius: theme.radii["2xl"],
            borderTopRightRadius: theme.radii["2xl"],
            boxShadow: theme.shadows.lg,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        },
        dragHandle: {
            position: "absolute",
            top: theme.space[2],
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
            width: "72px",
            height: "24px",
            border: 0,
            padding: 0,
            backgroundColor: "transparent",
            cursor: "grab",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            touchAction: "none",
        },
        dragHandleIndicator: {
            width: "40px",
            height: "4px",
            borderRadius: theme.radii.full,
            backgroundColor: colors.secondaryText,
        },
    })
}
